import React from 'react';
import { Link } from 'react-router-dom';
import * as Headless from '@headlessui/react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TicketIcon
} from '@heroicons/react/24/outline';
import { PlayIcon, PauseIcon } from '@heroicons/react/20/solid';
import { Badge } from '@oysterjs/ui/badge';
import {
  CallFieldsFragment,
  CallQualityFeedbackNegativeReason,
  CallTranscriptFieldsFragment,
  CallTranscriptRole,
  MessageRating,
  ServiceRequestState
} from '~/api/graphql';
import { submitCallRating } from '~/api/data/feedback/mutations';
import type { SubmitCallRatingInput } from '~/api/data/feedback/mutations';
import { ThumbsUpIcon, ThumbsDownIcon } from '~/components/icons';
import { FeedbackDialog, useFeedbackDialog } from '~/components/feedback-dialog';
import {
  CallStatusBadge,
  titleCase,
  formatDuration,
  getCallDurationSeconds,
  dateFormatter
} from './calls-columns';

type CallFeedbackRating = SubmitCallRatingInput['rating'];

export type ConnectedCall = CallFieldsFragment & CallTranscriptFieldsFragment;

const CALL_QUALITY_REASON_LABELS: Record<CallQualityFeedbackNegativeReason, string> = {
  [CallQualityFeedbackNegativeReason.PoorAudioQuality]: 'Poor audio quality, hard to hear',
  [CallQualityFeedbackNegativeReason.CaraRepeatedTooMuch]: 'Cara repeated herself too frequently',
  [CallQualityFeedbackNegativeReason.Misunderstanding]: "Cara didn't understand what I was saying",
  [CallQualityFeedbackNegativeReason.WrongWorkflow]: "Cara didn't ask the right questions",
  [CallQualityFeedbackNegativeReason.SlowResponses]: 'Slow response',
  [CallQualityFeedbackNegativeReason.ResolutionTooLong]: 'Time to resolution was too long',
  [CallQualityFeedbackNegativeReason.Other]: 'Other reason (specify in comment)'
};

export const hasConnectedCallConversation = (call: ConnectedCall): boolean =>
  !!call.summary || !!call.recordingUrl || call.transcript.length > 0;

export const ConnectedCallConversation = (props: { call: ConnectedCall }) => {
  const feedback = useFeedbackDialog<CallQualityFeedbackNegativeReason>({
    onSubmit: async ({ reasons, comment }) => {
      const payload: SubmitCallRatingInput = {
        callId: props.call.id,
        rating: MessageRating.Down,
        reasons,
        ...(comment ? { comment } : {})
      };
      await submitCallRating(payload);
    },
    onSubmitPositive: async () => {
      await submitCallRating({
        callId: props.call.id,
        rating: MessageRating.Up,
        reasons: []
      });
    },
    otherReasonValue: CallQualityFeedbackNegativeReason.Other
  });

  const [playAudio, setPlayAudio] = React.useState<{
    play: boolean;
    offsetSeconds?: number;
  } | null>(null);

  return (
    <>
      <dl>
        {props.call.summary && (
          <CallSummary
            call={props.call}
            rating={feedback.rating}
            playAudio={playAudio}
            setPlayAudio={setPlayAudio}
            onThumbClick={feedback.handleThumbClick}
            isSubmitting={feedback.isSubmitting}
            submitError={feedback.submitError}
          />
        )}
        {props.call.serviceRequests.length > 0 && <CallServiceRequests call={props.call} />}
        {props.call.transcript.length > 0 && (
          <CallTranscript call={props.call} playAudio={playAudio} setPlayAudio={setPlayAudio} />
        )}
      </dl>
      <FeedbackDialog
        open={feedback.showFeedbackDialog}
        onClose={feedback.handleFeedbackCancel}
        reasons={CALL_QUALITY_REASON_LABELS}
        selectedReasons={feedback.selectedReasons}
        feedbackComment={feedback.feedbackComment}
        isSubmitting={feedback.isSubmitting}
        submitError={feedback.submitError}
        isSubmitDisabled={feedback.isSubmitDisabled}
        onSubmit={feedback.handleFeedbackSubmit}
        onCancel={feedback.handleFeedbackCancel}
        onToggleReason={feedback.toggleReason}
        onCommentChange={feedback.setFeedbackComment}
        onClearError={() => feedback.setSubmitError(null)}
        title="What went wrong?"
        description="Help us improve by sharing what went wrong"
        commentPlaceholder="Additional comment"
        commentRows={4}
      />
    </>
  );
};

export const ConnectedCallDetail = (props: { call: ConnectedCall }) => {
  const feedback = useFeedbackDialog<CallQualityFeedbackNegativeReason>({
    onSubmit: async ({ reasons, comment }) => {
      const payload: SubmitCallRatingInput = {
        callId: props.call.id,
        rating: MessageRating.Down,
        reasons,
        ...(comment ? { comment } : {})
      };
      await submitCallRating(payload);
    },
    onSubmitPositive: async () => {
      await submitCallRating({
        callId: props.call.id,
        rating: MessageRating.Up,
        reasons: []
      });
    },
    otherReasonValue: CallQualityFeedbackNegativeReason.Other
  });

  const [playAudio, setPlayAudio] = React.useState<{
    play: boolean;
    offsetSeconds?: number;
  } | null>(null);

  return (
    <>
      <dl>
        <CallProperties call={props.call} />
        <CallAccountSummary call={props.call} />
        {props.call.summary && (
          <CallSummary
            call={props.call}
            rating={feedback.rating}
            playAudio={playAudio}
            setPlayAudio={setPlayAudio}
            onThumbClick={feedback.handleThumbClick}
            isSubmitting={feedback.isSubmitting}
            submitError={feedback.submitError}
          />
        )}
        {props.call.serviceRequests.length > 0 && <CallServiceRequests call={props.call} />}
        {props.call.transcript.length > 0 && (
          <CallTranscript call={props.call} playAudio={playAudio} setPlayAudio={setPlayAudio} />
        )}
      </dl>
      <FeedbackDialog
        open={feedback.showFeedbackDialog}
        onClose={feedback.handleFeedbackCancel}
        reasons={CALL_QUALITY_REASON_LABELS}
        selectedReasons={feedback.selectedReasons}
        feedbackComment={feedback.feedbackComment}
        isSubmitting={feedback.isSubmitting}
        submitError={feedback.submitError}
        isSubmitDisabled={feedback.isSubmitDisabled}
        onSubmit={feedback.handleFeedbackSubmit}
        onCancel={feedback.handleFeedbackCancel}
        onToggleReason={feedback.toggleReason}
        onCommentChange={feedback.setFeedbackComment}
        onClearError={() => feedback.setSubmitError(null)}
        title="What went wrong?"
        description="Help us improve by sharing what went wrong"
        commentPlaceholder="Additional comment"
        commentRows={4}
      />
    </>
  );
};

export const CallProperties = (props: { call: ConnectedCall }) => {
  const properties = [
    {
      title: 'Status',
      value: <CallStatusBadge status={props.call.status} />
    },
    {
      title: 'Timestamp',
      value: dateFormatter.format(new Date(props.call.startedAt))
    },
    {
      title: 'Duration',
      value: formatDuration(getCallDurationSeconds(props.call))
    },
    {
      title: 'End Reason',
      value: props.call.disconnectionReason ? titleCase(props.call.disconnectionReason) : ''
    },
    {
      title: 'User Sentiment',
      value: props.call.sentiment ? titleCase(props.call.sentiment) : ''
    }
  ].filter((property) => !!property.value);

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
      {properties.map((property) => (
        <div key={property.title} className="sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
            {property.title}
          </dt>
          <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
            {property.value}
          </dd>
        </div>
      ))}
    </div>
  );
};

export const CallAccountSummary = (props: { call: ConnectedCall }) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Caller</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
          {props.call.fromName || <div className="text-neutral-500 dark:text-neutral-400">—</div>}
        </dd>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Account</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
          {props.call.account?.account.name && (
            <Link to={`/app/accounts/${props.call.account.id}`} className="hover:underline">
              <div className="flex items-center gap-1">
                {props.call.account.account.name}
                <ArrowTopRightOnSquareIcon className="size-4" />
              </div>
            </Link>
          )}
          {!props.call.account?.account.name && (
            <div className="text-neutral-500 dark:text-neutral-400">—</div>
          )}
        </dd>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Phone Number</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
          {props.call.fromNumber || <div className="text-neutral-500 dark:text-neutral-400">—</div>}
        </dd>
      </div>
    </div>
  );
};

const CallSummary = (props: {
  call: ConnectedCall;
  rating: CallFeedbackRating | null;
  playAudio: { play: boolean; offsetSeconds?: number } | null;
  setPlayAudio: React.Dispatch<
    React.SetStateAction<{ play: boolean; offsetSeconds?: number } | null>
  >;
  onThumbClick: (value: CallFeedbackRating) => void;
  isSubmitting: boolean;
  submitError: string | null;
}) => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  React.useEffect(() => {
    if (props.playAudio?.play && props.playAudio?.offsetSeconds !== undefined) {
      if (audioRef.current) {
        audioRef.current.currentTime = props.playAudio.offsetSeconds;
        audioRef.current.play();
      }
    } else if (!props.playAudio?.play) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
  }, [props.playAudio]);

  const isThumbUpSelected = props.rating === MessageRating.Up;
  const isThumbDownSelected = props.rating === MessageRating.Down;

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Summary</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">{props.call.summary}</dd>
      </div>
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white hidden sr-only">
          Recording
        </dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
          {props.call.recordingUrl ? (
            <div className="mt-2 flex items-center gap-2">
              <audio
                controls
                className="min-w-0 flex-1 h-8 w-full"
                ref={audioRef}
                onPause={() => props.setPlayAudio((prev) => ({ ...prev, play: false }))}
                onEnded={() => props.setPlayAudio((prev) => ({ ...prev, play: false }))}
              >
                <source src={props.call.recordingUrl} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
              <a
                href={props.call.recordingUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download recording"
                className="shrink-0 p-1.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300"
              >
                <ArrowDownTrayIcon className="size-5" aria-hidden="true" />
              </a>
            </div>
          ) : (
            <div className="text-neutral-500 dark:text-neutral-400">—</div>
          )}
        </dd>
      </div>
      <div className="flex flex-col gap-1.5 mt-2">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">How did Cara do?</dt>
        <dd className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Headless.Button
              type="button"
              onClick={() => props.onThumbClick(MessageRating.Up)}
              className={`p-1.5 rounded-lg transition-colors duration-150 ${
                isThumbUpSelected
                  ? 'text-neutral-700 dark:text-neutral-300'
                  : 'text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5'
              }`}
              aria-label="Cara did well"
              aria-pressed={isThumbUpSelected}
              disabled={props.isSubmitting}
            >
              <ThumbsUpIcon className="w-4 h-4" filled={isThumbUpSelected} />
            </Headless.Button>
            <Headless.Button
              type="button"
              onClick={() => props.onThumbClick(MessageRating.Down)}
              className={`p-1.5 rounded-lg transition-colors duration-150 ${
                isThumbDownSelected
                  ? 'text-neutral-700 dark:text-neutral-300'
                  : 'text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-white/5'
              }`}
              aria-label="Cara could improve"
              aria-pressed={isThumbDownSelected}
              disabled={props.isSubmitting}
            >
              <ThumbsDownIcon className="w-4 h-4" filled={isThumbDownSelected} />
            </Headless.Button>
          </div>
          {props.isSubmitting && !props.submitError && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Saving feedback…</p>
          )}
          {props.submitError && (
            <p className="text-sm text-rose-600 dark:text-rose-400">{props.submitError}</p>
          )}
          {!props.submitError && props.rating && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Thanks for letting us know!
            </p>
          )}
        </dd>
      </div>
    </div>
  );
};

const ServiceRequestBadge = (props: { state: ServiceRequestState }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [ServiceRequestState.Open]: 'amber',
          [ServiceRequestState.Closed]: 'green'
        }[props.state] || 'neutral'
      }
    >
      {props.state}
    </Badge>
  );
};

const CallServiceRequests = (props: { call: ConnectedCall }) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col sm:gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Service Requests</dt>
        <dd className="text-sm/6 text-neutral-900 dark:text-white">
          <ul
            role="list"
            className="divide-y divide-neutral-100 dark:divide-neutral-700 rounded-md border border-neutral-200 dark:border-neutral-700"
          >
            {props.call.serviceRequests.map((serviceRequest) => (
              <li key={serviceRequest.id} className="py-4 pl-4 pr-5 text-sm/6">
                <div className="flex justify-between ">
                  <div className="flex flex-1">
                    <TicketIcon
                      aria-hidden="true"
                      className="size-6 shrink-0 text-neutral-400 dark:text-neutral-500"
                    />
                    <div className="ml-4 min-w-0">
                      <Link
                        to={`/app/servicing/${serviceRequest.id}/overview`}
                        className="truncate font-medium hover:underline"
                      >
                        {titleCase(serviceRequest.type)}
                      </Link>
                    </div>
                  </div>

                  <div className="ml-4 shrink-0">
                    <ServiceRequestBadge state={serviceRequest.state} />
                  </div>
                </div>
                <pre className="ml-10 text-neutral-500 dark:text-neutral-400 whitespace-break-spaces font-body">
                  {serviceRequest.details ||
                    serviceRequest.question?.question ||
                    serviceRequest.cancelPolicy?.reason}
                </pre>
              </li>
            ))}
          </ul>
        </dd>
      </div>
    </div>
  );
};

const consolidateTranscript = (transcript: CallTranscriptFieldsFragment['transcript']) => {
  const consolidatedTranscript: CallTranscriptFieldsFragment['transcript'] = [];

  transcript.forEach((message) => {
    if (message.role === CallTranscriptRole.ToolInvocation) {
      const result = transcript.find(
        (m) => m.role === CallTranscriptRole.ToolResult && m.toolCallId === message.toolCallId
      );
      if (result) {
        consolidatedTranscript.push({
          ...message,
          content: result.content
        });
      }
    } else if (message.role !== CallTranscriptRole.ToolResult) {
      consolidatedTranscript.push(message);
    }
  });

  return consolidatedTranscript;
};

const CallTranscript = (props: {
  call: ConnectedCall;
  playAudio: { play: boolean; offsetSeconds?: number } | null;
  setPlayAudio: React.Dispatch<
    React.SetStateAction<{ play: boolean; offsetSeconds?: number } | null>
  >;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Transcript</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 flex flex-col gap-2.5">
          {consolidateTranscript(props.call.transcript)
            .filter((message) => !!message.content)
            .map((message, index) => {
              if (message.role === CallTranscriptRole.User) {
                return (
                  <UserMessage
                    key={`user-${index}`}
                    message={message.content}
                    playAudio={props.playAudio}
                    onPlayAudio={() =>
                      props.setPlayAudio((prev) => ({
                        play: prev === null ? true : !prev.play,
                        offsetSeconds: message.startTimeOffsetSeconds
                      }))
                    }
                  />
                );
              }
              if (message.role === CallTranscriptRole.Agent) {
                return (
                  <AgentMessage
                    key={`agent-${index}`}
                    message={message.content}
                    playAudio={props.playAudio}
                    onPlayAudio={() =>
                      props.setPlayAudio((prev) => ({
                        play: prev === null ? true : !prev.play,
                        offsetSeconds: message.startTimeOffsetSeconds
                      }))
                    }
                  />
                );
              }
              if (message.role === CallTranscriptRole.ToolInvocation) {
                return (
                  <ToolInvocation
                    key={`tool-${index}`}
                    toolName={message.toolName || ''}
                    toolResultJson={message.content}
                    toolArgumentsJson={message.toolArgumentsJson || ''}
                  />
                );
              }
              return null;
            })}
        </dd>
      </div>
    </div>
  );
};

const UserMessage = (props: {
  message: string;
  playAudio: { play: boolean; offsetSeconds?: number } | null;
  onPlayAudio: () => void;
}) => {
  return (
    <div className="bg-neutral-100 dark:bg-neutral-700 p-4 rounded-lg group">
      <div className="flex justify-between">
        <div className="uppercase text-xs text-neutral-500 dark:text-neutral-400 font-medium">
          User
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium invisible group-hover:visible hover:text-neutral-700 dark:hover:text-neutral-300">
          <Headless.Button
            type="button"
            onClick={props.onPlayAudio}
            aria-label={
              props.playAudio?.play ? 'Pause user message audio' : 'Play user message audio'
            }
            title={props.playAudio?.play ? 'Pause audio' : 'Play audio'}
          >
            {props.playAudio?.play ? (
              <PauseIcon className="size-3.5" />
            ) : (
              <PlayIcon className="size-3.5" />
            )}
          </Headless.Button>
        </div>
      </div>
      <div className="text-sm/6 text-neutral-700 dark:text-neutral-300">{props.message}</div>
    </div>
  );
};

const AgentMessage = (props: {
  message: string;
  playAudio: { play: boolean; offsetSeconds?: number } | null;
  onPlayAudio: () => void;
}) => {
  return (
    <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg group">
      <div className="flex justify-between">
        <div className="uppercase text-xs text-blue-500 dark:text-blue-400 font-medium">Agent</div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium invisible group-hover:visible hover:text-neutral-700 dark:hover:text-neutral-300">
          <Headless.Button
            type="button"
            onClick={props.onPlayAudio}
            aria-label={
              props.playAudio?.play ? 'Pause agent message audio' : 'Play agent message audio'
            }
            title={props.playAudio?.play ? 'Pause audio' : 'Play audio'}
          >
            {props.playAudio?.play ? (
              <PauseIcon className="size-3.5" />
            ) : (
              <PlayIcon className="size-3.5" />
            )}
          </Headless.Button>
        </div>
      </div>
      <div className="text-sm/6 text-neutral-700 dark:text-neutral-300">{props.message}</div>
    </div>
  );
};

const ToolInvocation = (props: {
  toolName: string;
  toolArgumentsJson: string;
  toolResultJson: string;
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  let toolResultValue;

  try {
    toolResultValue = JSON.stringify(JSON.parse(props.toolResultJson), null, 2);
  } catch {
    toolResultValue = props.toolResultJson;
  }

  return (
    <div className="bg-indigo-50 dark:bg-indigo-500/10 p-4 rounded-lg">
      <div className="flex justify-between">
        <div className="uppercase text-xs text-indigo-500 dark:text-indigo-400 font-medium">
          {titleCase(props.toolName)}
        </div>
        <div className="text-xs text-indigo-500 dark:text-indigo-400 font-medium">
          <Headless.Button type="button" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <ChevronUpIcon className="size-4" />
            ) : (
              <ChevronDownIcon className="size-4" />
            )}
          </Headless.Button>
        </div>
      </div>
      <div className="text-neutral-700 dark:text-neutral-300 font-mono text-xs/5 mt-2">
        {props.toolArgumentsJson}
      </div>
      {isExpanded && (
        <pre className="text-neutral-700 dark:text-neutral-300 font-mono text-xs/5 border-l-4 border-indigo-200 dark:border-indigo-500/30 pl-4 mt-2 whitespace-pre-wrap">
          {toolResultValue}
        </pre>
      )}
    </div>
  );
};
